import aegisFieldUrl from './assets/cyberpunk/aegis-field.png';
import aegisSeedUrl from './assets/cyberpunk/aegis-seed.webp';
import affixArmoredCoreUrl from './assets/cyberpunk/affix-armored-core.webp';
import affixKineticOverclockUrl from './assets/cyberpunk/affix-kinetic-overclock.webp';
import affixResonantDefenseUrl from './assets/cyberpunk/affix-resonant-defense.webp';
import arcCoreUrl from './assets/cyberpunk/arc-core.webp';
import armoredBrickUrl from './assets/cyberpunk/armored-brick.png';
import blastCoreUrl from './assets/cyberpunk/blast-core.webp';
import buildBurstUrl from './assets/cyberpunk/build-burst.webp';
import buildFortressUrl from './assets/cyberpunk/build-fortress.webp';
import buildMasteryBadgeUrl from './assets/cyberpunk/build-mastery-badge.webp';
import buildSwarmUrl from './assets/cyberpunk/build-swarm.webp';
import chrononBatteryUrl from './assets/cyberpunk/chronon-battery.webp';
import corrosionCoreUrl from './assets/cyberpunk/corrosion-core.webp';
import cursedReactorUrl from './assets/cyberpunk/cursed-reactor.webp';
import damageUpUrl from './assets/cyberpunk/damage-up.png';
import duplicationSealUrl from './assets/cyberpunk/duplication-seal.webp';
import echoCounterUrl from './assets/cyberpunk/echo-counter.webp';
import eventEchoForgeUrl from './assets/cyberpunk/event-echo-forge.webp';
import eventGlassOverclockUrl from './assets/cyberpunk/event-glass-overclock.webp';
import eventNaniteRepairUrl from './assets/cyberpunk/event-nanite-repair.webp';
import eventVitalityExchangeUrl from './assets/cyberpunk/event-vitality-exchange.webp';
import extraLifeUrl from './assets/cyberpunk/extra-life.png';
import extraProjectileUrl from './assets/cyberpunk/extra-projectile.png';
import fissionBrickUrl from './assets/cyberpunk/fission-brick.png';
import fractalChamberUrl from './assets/cyberpunk/fractal-chamber.webp';
import gravityCoreUrl from './assets/cyberpunk/gravity-core.webp';
import guardianAlloyUrl from './assets/cyberpunk/guardian-alloy.webp';
import harvestLensUrl from './assets/cyberpunk/harvest-lens.webp';
import ionPulseUrl from './assets/cyberpunk/ion-pulse.png';
import kineticAnchorUrl from './assets/cyberpunk/kinetic-anchor.webp';
import normalBrickUrl from './assets/cyberpunk/normal-brick.png';
import novaSplitUrl from './assets/cyberpunk/nova-split.png';
import overdriveCoilUrl from './assets/cyberpunk/overdrive-coil.webp';
import paddleExpandUrl from './assets/cyberpunk/paddle-expand.png';
import paddleUrl from './assets/cyberpunk/paddle.png';
import phaseCoreUrl from './assets/cyberpunk/phase-core.webp';
import prismLatticeUrl from './assets/cyberpunk/prism-lattice.webp';
import prismSentinelBossUrl from './assets/cyberpunk/prism-sentinel-boss.webp';
import projectileUrl from './assets/cyberpunk/projectile.png';
import reboundCoreUrl from './assets/cyberpunk/rebound-core.webp';
import reinforcedBrickUrl from './assets/cyberpunk/reinforced-brick.webp';
import replicatorBrickUrl from './assets/cyberpunk/replicator-brick.png';
import reserveHeartUrl from './assets/cyberpunk/reserve-heart.webp';
import singularityBossUrl from './assets/cyberpunk/singularity-boss.png';
import singularityCrownUrl from './assets/cyberpunk/singularity-crown.webp';
import speedUpUrl from './assets/cyberpunk/speed-up.png';
import stabilityPurgeUrl from './assets/cyberpunk/stability-purge.webp';
import suppressorBrickUrl from './assets/cyberpunk/suppressor-brick.webp';
import swarmHostBossUrl from './assets/cyberpunk/swarm-host-boss.webp';

import type {
  ActiveAbilityId,
  BallCoreId,
  BossAffixId,
  BrickKind,
  BuildId,
  ObjectiveId,
  RelicId,
  RunCleanseId,
  RunEventId,
  SeasonChallengeId,
  UpgradeId,
} from '@brick-rogue/game-core';

export type BossVisualWave = 10 | 20 | 30;
export type ChallengeBadgeId = 'build-mastery';

const bosses = {
  10: prismSentinelBossUrl,
  20: swarmHostBossUrl,
  30: singularityBossUrl,
} as const satisfies Readonly<Record<BossVisualWave, string>>;

const bricks = {
  normal: normalBrickUrl,
  armored: armoredBrickUrl,
  reinforced: reinforcedBrickUrl,
  suppressor: suppressorBrickUrl,
  replicator: replicatorBrickUrl,
  fission: fissionBrickUrl,
} as const satisfies Readonly<Record<BrickKind, string>>;

const builds = {
  swarm: buildSwarmUrl,
  burst: buildBurstUrl,
  fortress: buildFortressUrl,
} as const satisfies Readonly<Record<BuildId, string>>;

const events = {
  'nanite-repair': eventNaniteRepairUrl,
  'vitality-exchange': eventVitalityExchangeUrl,
  'echo-forge': eventEchoForgeUrl,
  'glass-overclock': eventGlassOverclockUrl,
} as const satisfies Readonly<Record<RunEventId, string>>;

const cleanse = {
  'stability-purge': stabilityPurgeUrl,
} as const satisfies Readonly<Record<RunCleanseId, string>>;

const challengeBadges = {
  'build-mastery': buildMasteryBadgeUrl,
} as const satisfies Readonly<Record<ChallengeBadgeId, string>>;

const bossAffixes = {
  'armored-core': affixArmoredCoreUrl,
  'kinetic-overclock': affixKineticOverclockUrl,
  'resonant-defense': affixResonantDefenseUrl,
} as const satisfies Readonly<Record<BossAffixId, string>>;

const objectives = {
  'active-operator': ionPulseUrl,
  'boss-interceptor': singularityBossUrl,
  'event-cartographer': eventEchoForgeUrl,
  'fusion-engineer': arcCoreUrl,
  'curse-breaker': stabilityPurgeUrl,
  'thirty-wave-victor': buildMasteryBadgeUrl,
} as const satisfies Readonly<Record<ObjectiveId, string>>;

const seasonChallenges = {
  'skill-circuit': ionPulseUrl,
  'boss-breach': prismSentinelBossUrl,
  'event-expedition': eventEchoForgeUrl,
  'fusion-trial': arcCoreUrl,
} as const satisfies Readonly<Record<SeasonChallengeId, string>>;

export const CYBERPUNK_VISUAL_ASSETS = {
  gameplay: {
    paddle: paddleUrl,
    projectile: projectileUrl,
    bosses,
    bricks,
  },
  abilities: {
    'nova-split': novaSplitUrl,
    'aegis-field': aegisFieldUrl,
    'ion-pulse': ionPulseUrl,
  } satisfies Readonly<Record<ActiveAbilityId, string>>,
  ballCores: {
    arc: arcCoreUrl,
    phase: phaseCoreUrl,
    blast: blastCoreUrl,
    corrosion: corrosionCoreUrl,
    gravity: gravityCoreUrl,
    rebound: reboundCoreUrl,
  } satisfies Readonly<Record<BallCoreId, string>>,
  relics: {
    'cursed-reactor': cursedReactorUrl,
    'prism-lattice': prismLatticeUrl,
    'kinetic-anchor': kineticAnchorUrl,
    'overdrive-coil': overdriveCoilUrl,
    'guardian-alloy': guardianAlloyUrl,
    'reserve-heart': reserveHeartUrl,
    'harvest-lens': harvestLensUrl,
    'duplication-seal': duplicationSealUrl,
    'fractal-chamber': fractalChamberUrl,
    'chronon-battery': chrononBatteryUrl,
    'aegis-seed': aegisSeedUrl,
    'echo-counter': echoCounterUrl,
    'singularity-crown': singularityCrownUrl,
  } satisfies Readonly<Record<RelicId, string>>,
  upgrades: {
    'echo-chamber': extraProjectileUrl,
    'fission-matrix': novaSplitUrl,
    'kinetic-core': damageUpUrl,
    'overclock-drive': speedUpUrl,
    'wide-guard': paddleExpandUrl,
    'reserve-core': extraLifeUrl,
    'arc-core': arcCoreUrl,
    'arc-conductor': arcCoreUrl,
    'phase-core': phaseCoreUrl,
    'phase-lance': phaseCoreUrl,
    'blast-core': blastCoreUrl,
    'blast-shaping': blastCoreUrl,
    'corrosion-core': corrosionCoreUrl,
    'corrosion-catalyst': corrosionCoreUrl,
    'gravity-core': gravityCoreUrl,
    'gravity-lens': gravityCoreUrl,
    'rebound-core': reboundCoreUrl,
    'rebound-capacitor': reboundCoreUrl,
  } satisfies Readonly<Record<UpgradeId, string>>,
  builds,
  events,
  cleanse,
  challengeBadges,
  bossAffixes,
  objectives,
  seasonChallenges,
} as const;
